'use client';

import React, { useState, useEffect } from 'react';
import {
  AlertCircle,
  CheckCircle,
  ChevronDown,
  CircleAlert,
  FileText,
  LightbulbIcon,
  Plus,
  Sparkles,
  Tag,
  X,
  XCircle
} from 'lucide-react';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { TooltipWrapper, TooltipContent, TooltipTrigger } from '../ui/tooltip';
import { Badge } from '../ui/badge';
import { Separator } from '../ui/separator';
import {
  DropdownMenuWrapper,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '../ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogClose,
  DialogFooter
} from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { useSelector } from 'react-redux';
import fetchData from '../../utils/fetchData';
import DecisionGridList from './decision-grid-list';
import ActionButtons from './ActionButtons';
export function DecisionMatrixCard() {
  const [selectedNaics, setSelectedNaics] = useState({});
  const [menuItems, setMenuItems] = useState([]); // State for dynamic menu items
  const [selectedLOB, setSelectedLOB] = useState(''); // Initialize with an empty string
  const [showRecommendations, setShowRecommendations] = useState(false);
  const [showAddClassification, setShowAddClassification] = useState(false);
  const [activeCmi, setActiveCmi] = useState('');
  const [newClassification, setNewClassification] = useState({
    naicsCode: '',
    description: '',
    source: ''
  });
  const [responseData, setResponseData] = useState([]);
  const selectedApplicationId = useSelector(
    state => state.intakeData.selectedAppId
  );

  const selectedApplicationDetails = useSelector(
    state => state.intakeData?.selectedApplicationData
  );

  useEffect(() => {
    if (selectedApplicationId) {
      fetchData(
        `https://cipilot-lexai-api-318242574572.us-central1.run.app/insureai/ml/intake/applications/v2/customer_naics_cmi/?application_id=${selectedApplicationId}`,
        'GET'
      ).then(data => {
        setResponseData(data);
        if (data.length > 0) {
          setSelectedNaics(data[0]);
        }
      });
    }
  }, [selectedApplicationId]);

  useEffect(() => {
    const lobs = selectedApplicationDetails?.lob || '';
    const lobArray = lobs
      .split(',')
      .filter(
        lob =>
          lob.includes('Commercial General Liability') ||
          lob.includes('Commercial Property')
      );

    setMenuItems(lobArray); // Dynamically set menu items
    setSelectedLOB(lobArray[0]); // Set the first item as the default selected value
  }, [selectedApplicationDetails]);

  const handleLOBChange = lob => {
    // setSelectedLOB(lob); // Save the selected value
  };

  const openRecommendations = cmi => {
    setActiveCmi(cmi);
    setShowRecommendations(true);
  };

  const handleAddClassification = () => {
    setShowAddClassification(false);
    setNewClassification({
      naicsCode: '',
      description: '',
      source: ''
    });
  };

  return (
    <>
      <Card className='p-5 bg-white shadow-md'>
        <div className='flex justify-between items-start'>
          <div className='flex items-center gap-2'>
            <h3 className='text-base font-semibold flex items-center'>
              <FileText className='h-4 w-4 mr-2 text-[#FF5533]' />
              Decision Matrix
            </h3>
            {/* <Badge
              variant='outline'
              className='bg-blue-50 text-blue-600 border-blue-200 text-sm flex items-center gap-1'
            >
              <Sparkles className='h-3 w-3' />
              AI-Generated Data
            </Badge> */}
          </div>
          <div className='flex gap-2 items-center'>
            <span className='text-sm text-gray-500 mr-1'>Switch LOB:</span>
            <div className='relative'>
              <select
                value={selectedLOB}
                onChange={e => handleLOBChange(e.target.value)}
                className='appearance-none bg-white border border-gray-300 rounded-md py-1 pl-3 pr-8  hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent cursor-pointer'
              >
                {menuItems.map((lob, index) => (
                  <option key={index} value={lob}>
                    {lob}
                  </option>
                ))}
              </select>
              <ChevronDown className='absolute right-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-500 pointer-events-none' />
            </div>
          </div>
        </div>

        <div className='mb-6'>
          <div className='flex justify-between items-center mb-4'>
            <h3 className=' font-semibold flex items-center'>
              <Tag className='h-4 w-4 mr-1.5 text-[#FF5533]' />
              NAICS Classifications
            </h3>
            {/* <ActionButtons /> */}
          </div>

          <Separator className='my-4' />

          <div className='flex gap-2 overflow-x-auto pb-2'>
            {responseData.map(naics => (
              <div
                key={naics.naics}
                className={`p-3 rounded-md cursor-pointer transition-all duration-200 w-[180px] ${selectedNaics.naics === naics.naics
                  ? naics.source === 'llm'
                    ? 'border-b-2 border-orange-500'
                    : 'border-b-2 border-blue-500'
                  : ''
                  } ${naics.source === 'llm' ? 'bg-[#FFF8F6]' : 'bg-blue-50'}`}
                onClick={() => setSelectedNaics(naics)}
              >
                <div className='flex flex-col items-start'>
                  <div className='flex items-center justify-between w-full'>
                    <span className=' font-medium'>{naics.naics}</span>
                  </div>
                  <TooltipWrapper>
                    <TooltipTrigger asChild>
                      <p className='text-sm text-gray-600 mt-1 truncate w-full'>
                        {naics.naics_description || '---'}
                      </p>
                    </TooltipTrigger>
                    <TooltipContent side='bottom' className='max-w-[300px] p-2'>
                      <p className='text-sm'>
                        {naics.naics_description || '---'}
                      </p>
                    </TooltipContent>
                  </TooltipWrapper>
                </div>
              </div>
            ))}
          </div>

          <div className='grid grid-cols-12 gap-4 mt-4'>
            <div className='col-span-5'>
              <Card
                className={`p-4 border shadow-md h-[350px] ${selectedNaics.source === 'llm' ? 'bg-[#FFF8F6]' : 'bg-blue-50'
                  }`}
              >
                <div className='flex justify-between items-start mb-3'>
                  <h4 className='text-sm font-medium text-gray-700'>
                    Classification Details
                  </h4>
                  <Badge
                    variant='outline'
                    className={`${selectedNaics.source === 'llm'
                      ? 'bg-[#FFF8F6] text-[#FF5533] border-[#FFDED6]'
                      : 'bg-blue-50 text-blue-700 border-blue-200'
                      }`}
                  >
                    NAICS {selectedNaics.naics}
                  </Badge>
                </div>

                <div className='space-y-3 overflow-y-auto max-h-[280px] pr-1'>
                  <div className='bg-white p-3 rounded-md border border-gray-100'>
                    <p className='text-sm text-gray-500 mb-1'>Description</p>
                    <p className='text-sm font-medium'>
                      {selectedNaics.naics_description || '---'}
                    </p>
                  </div>
                  <div className='bg-white p-3 rounded-md border border-gray-100'>
                    <p className='text-sm text-gray-500 mb-1'>Source</p>
                    <p className='text-sm font-medium'>
                      <Badge
                        variant='outline'
                        className={`${selectedNaics.source === 'llm'
                          ? 'bg-[#FFF8F6] text-[#FF5533] border-[#FFDED6]'
                          : 'bg-blue-50 text-blue-700 border-blue-200'
                          }`}
                      >
                        {selectedNaics.source === 'llm'
                          ? 'NativeOrange'
                          : 'Application'}
                      </Badge>
                    </p>
                  </div>
                  <div className='bg-white p-3 rounded-md border border-gray-100'>
                    <p className='text-sm text-gray-500 mb-1'>
                      Confidence Level
                    </p>
                    <p className='text-sm font-medium'>--%</p>
                  </div>
                  <div className='bg-white p-3 rounded-md border border-gray-100'>
                    <p className='text-sm text-gray-500 mb-1'>
                      Related Documents
                    </p>
                    <p className='text-sm font-medium'>---</p>
                  </div>
                </div>
              </Card>
            </div>

            {/* Right side: CMI cards (was on left) */}
            <div className='col-span-7'>
              <Card
                className={`p-4 border shadow-md h-[350px] ${selectedNaics.source === 'llm' ? 'bg-[#FFF8F6]' : 'bg-blue-50'
                  }`}
              >
                <div className='flex justify-between items-center mb-3'>
                  <h4 className='text-sm font-medium text-gray-700'>
                    CMI Details for NAICS {selectedNaics.naics}
                  </h4>
                </div>
                <div className='grid grid-cols-2 gap-3 overflow-y-auto max-h-[280px] pr-1'>
                  {selectedNaics.cmis?.map((cmi, index) => (
                    <Card
                      key={index}
                      className={`p-3  border flex flex-col  ${cmi.selected_cmi
                        ? 'border-[green-600] bg-green-50'
                        : 'border-gray-100 bg-white'
                        }`}
                    >
                      <div>
                        <h4 className='text-sm font-medium mb-1 flex items-center justify-between'>
                          <span>CMI {cmi.cmi}</span>
                          <span>ISOGL: {cmi.iso || '--'}</span>
                          <Badge
                            className={`${cmi.appetite_decision === 'Out of Appetite'
                              ? 'bg-red-100 text-red-700 border-red-200'
                              : cmi.appetite_decision === 'Conditional'
                                ? 'bg-yellow-100 text-yellow-700 border-yellow-200'
                                : 'bg-green-100 text-green-700 border-green-200'
                              } text-[10px]`}
                          >
                            {cmi.appetite_decision || 'In Appetite'}
                          </Badge>
                        </h4>
                        <p className='text-sm mb-2 mt-4'>
                          {cmi.cmi_description
                            ? cmi.cmi_description
                              .split('. ')
                              .map(
                                sentence =>
                                  sentence.charAt(0).toUpperCase() +
                                  sentence.slice(1).toLowerCase()
                              )
                              .join('. ')
                            : '---'}
                        </p>
                      </div>
                      <div className='mt-auto'>
                        <div className='grid grid-cols-3 gap-1 border-t pt-2'>
                          <div>
                            <p className='text-[10px] text-gray-500'>
                              Property
                            </p>
                            <p className='text-[10px] font-medium'>
                              {cmi?.lobs?.property || '--'}
                            </p>
                          </div>
                          <div>
                            <p className='text-[10px] text-gray-500'>GL Prem</p>
                            <p className='text-[10px] font-medium'>
                              {cmi?.lobs?.gl_prem_op || '--'}
                            </p>
                          </div>
                          <div>
                            <p className='text-[10px] text-gray-500'>GL Prod</p>
                            <p className='text-[10px] font-medium'>
                              {cmi?.lobs?.gl_prod_op || '--'}
                            </p>
                          </div>
                        </div>
                      </div>
                    </Card>
                  )) || <></>}
                </div>
              </Card>
            </div>
          </div>
        </div>

        <Separator className='mb-6' />
        <DecisionGridList selectedLOB={selectedLOB} />
      </Card>

      <Dialog open={showRecommendations} onOpenChange={setShowRecommendations}>
        <DialogContent className='sm:max-w-md bg-white'>
          <DialogHeader>
            <DialogTitle className='flex items-center'>
              <LightbulbIcon className='h-5 w-5 mr-2 text-yellow-500' />
              Recommendations for CMI {activeCmi}
            </DialogTitle>
            <DialogDescription>
              Review and implement these recommendations to improve risk
              profile.
            </DialogDescription>
          </DialogHeader>

          <div className='mt-4 space-y-4'>
            {activeCmi &&
              selectedNaics.cmis
                ?.find(cmi => cmi.cmi === activeCmi)
                ?.recommendations?.map((rec, index) => (
                  <div key={index} className='border rounded-md p-3 bg-white'>
                    <div className='flex justify-between items-start mb-2'>
                      <h3 className=' font-medium'>{rec.title}</h3>
                      <Badge
                        variant='outline'
                        className={`${rec.impact === 'High'
                          ? 'bg-red-50 text-red-700 border-red-200'
                          : ''
                          } ${rec.impact === 'Medium'
                            ? 'bg-yellow-50 text-yellow-700 border-yellow-200'
                            : ''
                          } ${rec.impact === 'Low'
                            ? 'bg-green-50 text-green-700 border-green-200'
                            : ''
                          }`}
                      >
                        {rec.impact} Impact
                      </Badge>
                    </div>
                    <p className=' text-gray-600 mb-2'>
                      {rec.description}
                    </p>
                    <div className='flex justify-between items-center'>
                      <Badge
                        variant='outline'
                        className='bg-gray-50 text-gray-700 border-gray-200'
                      >
                        {rec.type}
                      </Badge>
                      <div className='flex gap-2'>
                        <Button
                          variant='outline'
                          size='sm'
                          className='text-sm h-7'
                        >
                          Dismiss
                        </Button>
                        <Button
                          size='sm'
                          className='text-sm h-7 bg-[#FF5533] hover:bg-[#E04A2F] text-white'
                        >
                          Implement
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
          </div>

          <DialogClose asChild>
            <Button variant='ghost' size='sm'>
              <X className='h-4 w-4 mr-1' />
            </Button>
          </DialogClose>
        </DialogContent>
      </Dialog>

      <Dialog
        open={showAddClassification}
        onOpenChange={setShowAddClassification}
      >
        <DialogContent className='sm:max-w-md bg-white'>
          <DialogHeader>
            <DialogTitle className='flex items-center'>
              <Plus className='h-5 w-5 mr-2 text-[#FF5533]' />
              Add New Classification
            </DialogTitle>
            <DialogDescription>
              Enter the details for the new classification.
            </DialogDescription>
          </DialogHeader>

          <div className='grid gap-4 py-4'>
            <div className='grid grid-cols-4 items-center gap-4'>
              <Label htmlFor='naicsCode' className='text-right'>
                NAICS Code
              </Label>
              <Input
                id='naicsCode'
                placeholder='e.g. 115112'
                className='col-span-3'
                value={newClassification.naicsCode}
                onChange={e =>
                  setNewClassification({
                    ...newClassification,
                    naicsCode: e.target.value
                  })
                }
              />
            </div>
            <div className='grid grid-cols-4 items-center gap-4'>
              <Label htmlFor='description' className='text-right'>
                Description
              </Label>
              <Input
                id='description'
                placeholder='e.g. Soil Preparation, Planting, and Cultivating'
                className='col-span-3'
                value={newClassification.description}
                onChange={e =>
                  setNewClassification({
                    ...newClassification,
                    description: e.target.value
                  })
                }
              />
            </div>
            <div className='grid grid-cols-4 items-center gap-4'>
              <Label htmlFor='source' className='text-right'>
                Source ==
              </Label>
              <div className='col-span-3 flex gap-2'>
                <Button
                  type='button'
                  variant={
                    newClassification.source === 'llm' ? 'outline' : 'default'
                  }
                  size='sm'
                  className={
                    newClassification.source !== 'llm'
                      ? 'bg-[#FF5533] hover:bg-[#E04A2F]  text-white'
                      : ''
                  }
                  onClick={() =>
                    setNewClassification({
                      ...newClassification,
                      source: 'Manual'
                    })
                  }
                >
                  Manual
                </Button>
                <Button
                  type='button'
                  variant={
                    newClassification.source === 'Application'
                      ? 'default'
                      : 'outline'
                  }
                  size='sm'
                  className={
                    newClassification.source === 'Application'
                      ? 'bg-[#FF5533] hover:bg-[#E04A2F]  text-white'
                      : ''
                  }
                  onClick={() =>
                    setNewClassification({
                      ...newClassification,
                      source: 'Application'
                    })
                  }
                >
                  Application
                </Button>
                <Button
                  type='button'
                  variant={
                    newClassification.source === 'llm' ? 'default' : 'outline'
                  }
                  size='sm'
                  className={
                    newClassification.source === 'llm'
                      ? 'bg-[#FF5533] hover:bg-[#E04A2F]  text-white'
                      : ''
                  }
                  onClick={() =>
                    setNewClassification({
                      ...newClassification,
                      source: 'llm'
                    })
                  }
                >
                  NativeOrange
                </Button>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant='outline'
              size='sm'
              onClick={() => setShowAddClassification(false)}
            >
              Cancel
            </Button>
            <Button
              size='sm'
              className='bg-[#FF5533] hover:bg-[#E04A2F] text-white'
              onClick={handleAddClassification}
              disabled={
                !newClassification.naicsCode || !newClassification.description
              }
            >
              Add Classification
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
